import { useMemo } from 'react';
import { Shield, Ban, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Label } from 'components/ui/label';
import { useGetGuardrailsCatalog } from 'features/chat/hooks/use-agents';
import type {
  GuardrailConfig,
  GuardrailOutputStrategy,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import { getAskAgentOwnerToAddMessage } from './ask-agent-owner-message';
import { AgentConfigGuardrailCard } from './agent-config-guardrail-card';

type GuardrailsSectionProps = {
  enabled: boolean;
  canEdit?: boolean;
  outputStrategy: GuardrailOutputStrategy;
  onOutputStrategyChange?: (strategy: GuardrailOutputStrategy) => void;
  guardrails: Record<string, GuardrailConfig>;
  onGuardrailsChange?: (guardrails: Record<string, GuardrailConfig>) => void;
  errors?: Record<string, string>;
};

export function GuardrailsSection({
  enabled,
  canEdit = true,
  outputStrategy,
  onOutputStrategyChange,
  guardrails,
  onGuardrailsChange,
  errors,
}: GuardrailsSectionProps) {
  const { t } = useTranslation();
  const { data: catalog = [] } = useGetGuardrailsCatalog();

  const catalogMap = useMemo(() => {
    const map = new Map<string, (typeof catalog)[0]>();
    for (const item of catalog) {
      map.set(item.id, item);
    }
    return map;
  }, [catalog]);

  const configuredEntries = useMemo(() => Object.entries(guardrails), [guardrails]);

  const handleToggleGuardrail = (id: string, isEnabled: boolean) => {
    const current = guardrails[id] || { enabled: true };
    onGuardrailsChange?.({
      ...guardrails,
      [id]: {
        ...current,
        enabled: isEnabled,
      },
    });
  };

  const handleRemoveGuardrail = (id: string) => {
    const updated = { ...guardrails };
    delete updated[id];
    onGuardrailsChange?.(updated);
  };

  const handleConfigChange = (id: string, updatedConfig: GuardrailConfig) => {
    onGuardrailsChange?.({
      ...guardrails,
      [id]: updatedConfig,
    });
  };

  const isInteractive = enabled && canEdit;

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="space-y-3">
        <Label className="text-xs font-semibold text-[#3d3d3d] dark:text-true-white">
          {t('GUARDRAIL_OUTPUT_STRATEGY', { defaultValue: 'Matched Output Strategy' })}
        </Label>
        <p className="text-xs text-[#6d6d6d] dark:text-muted-foreground">
          {t('GUARDRAIL_OUTPUT_STRATEGY_DESC', {
            defaultValue:
              'Choose whether an assistant response that triggers a guardrail is completely blocked or redacted.',
          })}
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            disabled={!isInteractive}
            onClick={() => onOutputStrategyChange?.('block')}
            className={`flex items-start gap-3 rounded-lg border p-3.5 text-left transition-all ${
              outputStrategy === 'block'
                ? 'border-primary bg-primary/5 ring-1 ring-primary dark:border-primary dark:bg-primary/20'
                : 'border-[#e7e7e7] bg-card hover:bg-muted/50 dark:border-border dark:bg-card'
            } ${!isInteractive ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <div
              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
                outputStrategy === 'block'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground dark:bg-white/10 dark:text-muted-foreground'
              }`}
            >
              <Ban className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-[#3d3d3d] dark:text-true-white">
                {t('STRATEGY_BLOCK', { defaultValue: 'Block Response' })}
              </div>
              <p className="mt-0.5 text-xs leading-relaxed text-[#6d6d6d] dark:text-muted-foreground">
                {t('STRATEGY_BLOCK_DESC', {
                  defaultValue: 'Prevents delivery of the assistant answer if any guardrail is violated.',
                })}
              </p>
            </div>
          </button>

          <button
            type="button"
            disabled={!isInteractive}
            onClick={() => onOutputStrategyChange?.('redact')}
            className={`flex items-start gap-3 rounded-lg border p-3.5 text-left transition-all ${
              outputStrategy === 'redact'
                ? 'border-primary bg-primary/5 ring-1 ring-primary dark:border-primary dark:bg-primary/20'
                : 'border-[#e7e7e7] bg-card hover:bg-muted/50 dark:border-border dark:bg-card'
            } ${!isInteractive ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <div
              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
                outputStrategy === 'redact'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground dark:bg-white/10 dark:text-muted-foreground'
              }`}
            >
              <EyeOff className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-[#3d3d3d] dark:text-true-white">
                {t('STRATEGY_REDACT', { defaultValue: 'Redact Matching Content' })}
              </div>
              <p className="mt-0.5 text-xs leading-relaxed text-[#6d6d6d] dark:text-muted-foreground">
                {t('STRATEGY_REDACT_DESC', {
                  defaultValue: 'Masks only the sensitive parts or keywords while sending the remainder.',
                })}
              </p>
            </div>
          </button>
        </div>
      </div>

      <div className="w-full min-w-0 space-y-4 border-t border-[#e7e7e7] pt-4 dark:border-border">
        <Label className="text-xs font-semibold text-[#3d3d3d] dark:text-true-white">
          {t('ACTIVE_GUARDRAILS', { defaultValue: 'Active Guardrails' })}
        </Label>

        {configuredEntries.length > 0 ? (
          <div className="grid w-full min-w-0 gap-3">
            {configuredEntries.map(([id, config]) => {
              const catalogItem = catalogMap.get(id);
              return (
                <AgentConfigGuardrailCard
                  key={id}
                  guardrailId={id}
                  enabled={config.enabled}
                  onToggleEnabled={(val) => handleToggleGuardrail(id, val)}
                  onRemove={() => handleRemoveGuardrail(id)}
                  disabled={!isInteractive}
                  params={catalogItem?.params}
                  config={config}
                  onConfigChange={(updated) => handleConfigChange(id, updated)}
                  errorMessage={errors?.[id]}
                />
              );
            })}
          </div>
        ) : (
          <div
            className={`flex h-[141px] w-full min-w-0 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[#e7e7e7] bg-[#f6f6f6] px-4 text-center dark:border-border dark:bg-muted/30 ${
              !enabled ? 'opacity-50' : ''
            }`}
          >
            <Shield className="size-8 text-[#6d6d6d] dark:text-muted-foreground" strokeWidth={1.5} />
            <p className="max-w-[400px] text-xs font-medium leading-[1.6] text-[#6d6d6d] dark:text-muted-foreground">
              {canEdit
                ? t('NO_GUARDRAILS_ADDED_CLICK_ADD_TO_GET_STARTED', {
                    defaultValue: "No guardrails added yet. Click 'Add' to get started.",
                  })
                : getAskAgentOwnerToAddMessage(t, t('GUARDRAILS', { defaultValue: 'guardrails' }))}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
